import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useApiClient } from '../api/ApiClientProvider';
import type { ApiError } from '../api/ApiError';
import { garageKeys } from './queryKeys';
import type {
  Modification,
  SaveModificationBody,
  SaveVehicleBody,
  SaveWishlistBody,
  VehicleDetail,
  VehiclePhoto,
  WishlistItem,
} from './types';
import { unwrap, unwrapEmpty } from './unwrap';

/**
 * Invalidates everything a change to one vehicle can affect.
 *
 * The list carries a modification count, a cover thumbnail and an updated timestamp, so almost any
 * edit changes it too. Invalidating both is cheaper than reasoning about which fields moved.
 */
function useInvalidateVehicle(): (vehicleId: string) => Promise<void> {
  const queryClient = useQueryClient();

  return async (vehicleId: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: garageKeys.vehicle(vehicleId) }),
      queryClient.invalidateQueries({ queryKey: garageKeys.list() }),
    ]);
  };
}

/** Creates a vehicle. */
export function useCreateVehicle(): UseMutationResult<VehicleDetail, ApiError, SaveVehicleBody> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<VehicleDetail, ApiError, SaveVehicleBody>({
    mutationFn: async (body) =>
      unwrap(await client.POST('/api/vehicles', { body }), 'Could not add that vehicle'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: garageKeys.list() });
    },
  });
}

/** Updates a vehicle. */
export function useUpdateVehicle(
  vehicleId: string,
): UseMutationResult<VehicleDetail, ApiError, SaveVehicleBody> {
  const client = useApiClient();
  const invalidate = useInvalidateVehicle();

  return useMutation<VehicleDetail, ApiError, SaveVehicleBody>({
    mutationFn: async (body) =>
      unwrap(
        await client.PUT('/api/vehicles/{vehicleId}', { params: { path: { vehicleId } }, body }),
        'Could not save that vehicle',
      ),
    onSuccess: async () => invalidate(vehicleId),
  });
}

/** Deletes a vehicle. */
export function useDeleteVehicle(): UseMutationResult<void, ApiError, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: async (vehicleId) =>
      unwrapEmpty(
        await client.DELETE('/api/vehicles/{vehicleId}', { params: { path: { vehicleId } } }),
        'Could not delete that vehicle',
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: garageKeys.all });
    },
  });
}

/** Adds a modification to a vehicle. */
export function useAddModification(
  vehicleId: string,
): UseMutationResult<Modification, ApiError, SaveModificationBody> {
  const client = useApiClient();
  const invalidate = useInvalidateVehicle();

  return useMutation<Modification, ApiError, SaveModificationBody>({
    mutationFn: async (body) =>
      unwrap(
        await client.POST('/api/vehicles/{vehicleId}/modifications', {
          params: { path: { vehicleId } },
          body,
        }),
        'Could not add that modification',
      ),
    onSuccess: async () => invalidate(vehicleId),
  });
}

/** The identifier and new body sent when updating a modification. */
export interface UpdateModificationInput {
  readonly modificationId: string;
  readonly body: SaveModificationBody;
}

/**
 * Updates a modification.
 *
 * The body carries the `version` read with the row, so a concurrent edit returns 409 rather than
 * silently overwriting the other change.
 */
export function useUpdateModification(
  vehicleId: string,
): UseMutationResult<Modification, ApiError, UpdateModificationInput> {
  const client = useApiClient();
  const invalidate = useInvalidateVehicle();

  return useMutation<Modification, ApiError, UpdateModificationInput>({
    mutationFn: async ({ modificationId, body }) =>
      unwrap(
        await client.PUT('/api/vehicles/{vehicleId}/modifications/{modificationId}', {
          params: { path: { vehicleId, modificationId } },
          body,
        }),
        'Could not save that modification',
      ),
    onSuccess: async () => invalidate(vehicleId),
  });
}

/** Removes a modification from a vehicle. */
export function useDeleteModification(
  vehicleId: string,
): UseMutationResult<void, ApiError, string> {
  const client = useApiClient();
  const invalidate = useInvalidateVehicle();

  return useMutation<void, ApiError, string>({
    mutationFn: async (modificationId) =>
      unwrapEmpty(
        await client.DELETE('/api/vehicles/{vehicleId}/modifications/{modificationId}', {
          params: { path: { vehicleId, modificationId } },
        }),
        'Could not remove that modification',
      ),
    onSuccess: async () => invalidate(vehicleId),
  });
}

/** Adds a wishlist item to a vehicle. */
export function useAddWishlistItem(
  vehicleId: string,
): UseMutationResult<WishlistItem, ApiError, SaveWishlistBody> {
  const client = useApiClient();
  const invalidate = useInvalidateVehicle();

  return useMutation<WishlistItem, ApiError, SaveWishlistBody>({
    mutationFn: async (body) =>
      unwrap(
        await client.POST('/api/vehicles/{vehicleId}/wishlist', {
          params: { path: { vehicleId } },
          body,
        }),
        'Could not add that wishlist item',
      ),
    onSuccess: async () => invalidate(vehicleId),
  });
}

/** The identifier and new body sent when updating a wishlist item. */
export interface UpdateWishlistInput {
  readonly wishlistItemId: string;
  readonly body: SaveWishlistBody;
}

/**
 * Updates a wishlist item.
 *
 * The body carries the `version` read with the row, so a concurrent edit returns 409 rather than
 * silently overwriting the other change.
 */
export function useUpdateWishlistItem(
  vehicleId: string,
): UseMutationResult<WishlistItem, ApiError, UpdateWishlistInput> {
  const client = useApiClient();
  const invalidate = useInvalidateVehicle();

  return useMutation<WishlistItem, ApiError, UpdateWishlistInput>({
    mutationFn: async ({ wishlistItemId, body }) =>
      unwrap(
        await client.PUT('/api/vehicles/{vehicleId}/wishlist/{wishlistItemId}', {
          params: { path: { vehicleId, wishlistItemId } },
          body,
        }),
        'Could not save that wishlist item',
      ),
    onSuccess: async () => invalidate(vehicleId),
  });
}

/** Removes a wishlist item from a vehicle. */
export function useDeleteWishlistItem(
  vehicleId: string,
): UseMutationResult<void, ApiError, string> {
  const client = useApiClient();
  const invalidate = useInvalidateVehicle();

  return useMutation<void, ApiError, string>({
    mutationFn: async (wishlistItemId) =>
      unwrapEmpty(
        await client.DELETE('/api/vehicles/{vehicleId}/wishlist/{wishlistItemId}', {
          params: { path: { vehicleId, wishlistItemId } },
        }),
        'Could not remove that wishlist item',
      ),
    onSuccess: async () => invalidate(vehicleId),
  });
}

/**
 * Promotes a wishlist item into an installed modification.
 *
 * The server keeps the original estimate on the wishlist item and records what was actually paid on
 * the new modification, so both numbers survive.
 */
export function useInstallWishlistItem(
  vehicleId: string,
): UseMutationResult<unknown, ApiError, { readonly wishlistItemId: string; readonly cost?: number }> {
  const client = useApiClient();
  const invalidate = useInvalidateVehicle();

  return useMutation<unknown, ApiError, { readonly wishlistItemId: string; readonly cost?: number }>({
    mutationFn: async ({ wishlistItemId, cost }) =>
      unwrap(
        await client.POST('/api/vehicles/{vehicleId}/wishlist/{wishlistItemId}/install', {
          params: { path: { vehicleId, wishlistItemId } },
          body: { cost: cost ?? null, installedDate: null, installer: null },
        }),
        'Could not mark that as installed',
      ),
    onSuccess: async () => invalidate(vehicleId),
  });
}

/**
 * Uploads a photo.
 *
 * Sent as multipart form data with the body serializer disabled, because the default JSON
 * serializer would stringify the `FormData` and the file would never leave the browser.
 */
export function useUploadPhoto(vehicleId: string): UseMutationResult<VehiclePhoto, ApiError, File> {
  const client = useApiClient();
  const invalidate = useInvalidateVehicle();

  return useMutation<VehiclePhoto, ApiError, File>({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append('file', file);

      return unwrap(
        await client.POST('/api/vehicles/{vehicleId}/photos', {
          params: { path: { vehicleId } },
          body: form as never,
          bodySerializer: (body: unknown) => body as FormData,
        }),
        'Could not upload that photo',
      );
    },
    onSuccess: async () => invalidate(vehicleId),
  });
}

/** Removes a photo. */
export function useDeletePhoto(vehicleId: string): UseMutationResult<void, ApiError, string> {
  const client = useApiClient();
  const invalidate = useInvalidateVehicle();

  return useMutation<void, ApiError, string>({
    mutationFn: async (photoId) =>
      unwrapEmpty(
        await client.DELETE('/api/vehicles/{vehicleId}/photos/{photoId}', {
          params: { path: { vehicleId, photoId } },
        }),
        'Could not remove that photo',
      ),
    onSuccess: async () => invalidate(vehicleId),
  });
}

/** Sets the display order of a vehicle's gallery. */
export function useReorderPhotos(
  vehicleId: string,
): UseMutationResult<VehiclePhoto[], ApiError, string[]> {
  const client = useApiClient();
  const invalidate = useInvalidateVehicle();

  return useMutation<VehiclePhoto[], ApiError, string[]>({
    mutationFn: async (photoIds) =>
      unwrap(
        await client.PUT('/api/vehicles/{vehicleId}/photos/order', {
          params: { path: { vehicleId } },
          body: { photoIds },
        }),
        'Could not reorder the gallery',
      ),
    onSuccess: async () => invalidate(vehicleId),
  });
}
